/**
 * RoleChecklist — elige uno o varios roles funcionales (OT-0058 §4.3).
 *
 * Una persona puede tener varios roles en el mismo proyecto: quien prepara
 * la preventa también puede ser quien la aprueba como gerente comercial.
 * Lo usan los diálogos de alta y el de editar roles de un miembro.
 *
 * Un rol elegido que ya no está en `roles` (catálogo que cambió) se sigue
 * mostrando, con su clave, para que se pueda conservar o quitar.
 */

import { useTranslation } from 'react-i18next'

import { roleLabel, type RoleOption } from './roles'

interface RoleChecklistProps {
  roles: RoleOption[]
  value: string[]
  onChange: (next: string[]) => void
  disabled?: boolean
  /** Nombre accesible del grupo (el label visible lo pone el caller). */
  ariaLabel: string
}

export function RoleChecklist({
  roles,
  value,
  onChange,
  disabled = false,
  ariaLabel,
}: RoleChecklistProps) {
  const { t } = useTranslation('projects')
  const known = new Set(roles.map((r) => r.template_key))
  const options = [
    ...value.filter((key) => !known.has(key)).map((key) => ({ key, name: key })),
    ...roles.map((r) => ({ key: r.template_key, name: roleLabel(r.template_key, roles) })),
  ]

  function toggle(key: string, checked: boolean) {
    onChange(checked ? [...value, key] : value.filter((k) => k !== key))
  }

  if (options.length === 0) {
    return (
      <p className="mt-1 font-body text-sm text-ink-muted italic">
        {t('team.roles.none_available')}
      </p>
    )
  }

  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className="mt-1 max-h-48 overflow-y-auto rounded-md border border-line bg-canvas divide-y divide-line"
    >
      {options.map((o) => (
        <label
          key={o.key}
          className="flex items-center gap-2 px-3 py-2 text-sm text-ink cursor-pointer hover:bg-surface-muted"
        >
          <input
            type="checkbox"
            checked={value.includes(o.key)}
            onChange={(e) => toggle(o.key, e.target.checked)}
            disabled={disabled}
            className="w-4 h-4 rounded border-line text-brand focus:ring-brand focus:ring-offset-0"
          />
          <span className="truncate">{o.name}</span>
        </label>
      ))}
    </div>
  )
}
